import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useReadOnly, ReadOnlyBanner } from "../hooks/useReadOnly";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Trash2, Edit2, X, Eraser } from "lucide-react";

function Modal({ open, onClose, title, children, width = 560 }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)" }} onClick={onClose} />
      <div style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: width, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CleanupForm({ initial, onSave, onCancel, departments, userRole }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [deptId, setDeptId] = useState(initial?.department_id || "");
  const [wordsRaw, setWordsRaw] = useState((initial?.words || []).join("\n"));
  const [caseSensitive, setCaseSensitive] = useState(initial?.case_sensitive !== undefined ? initial.case_sensitive : true);
  const [wholeWord, setWholeWord] = useState(initial?.whole_word !== undefined ? initial.whole_word : true);
  const [error, setError] = useState(""); const [saving, setSaving] = useState(false);

  const submit = async () => {
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    const words = wordsRaw.split("\n").map(w => w.trim()).filter(Boolean);
    if (words.length === 0) { setError("Add at least one word/phrase"); return; }
    setSaving(true);
    try {
      await onSave({ name, description, department_id: deptId || null, words, case_sensitive: caseSensitive, whole_word: wholeWord });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div>
      {error && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", padding: "0.5rem 0.75rem", marginBottom: "0.75rem", fontSize: "0.85rem", color: "#991B1B" }}>{error}</div>}
      <div style={{ display: "grid", gridTemplateColumns: userRole === "superadmin" ? "2fr 1fr" : "1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
        <div>
          <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Name *</label>
          <input data-testid="ct-name" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Company Suffixes" />
        </div>
        {userRole === "superadmin" && (
          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Department</label>
            <select data-testid="ct-dept" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }} value={deptId} onChange={e => setDeptId(e.target.value)}>
              <option value="">— All —</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div style={{ marginBottom: "0.75rem" }}>
        <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Description</label>
        <input className="recon-input" style={{ width: "100%", boxSizing: "border-box" }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
      </div>
      <div style={{ marginBottom: "0.75rem" }}>
        <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>
          Words / phrases to remove (one per line) — {wordsRaw.split("\n").filter(w => w.trim()).length} entries
        </label>
        <textarea data-testid="ct-words" className="recon-input" style={{ width: "100%", boxSizing: "border-box", minHeight: "180px", fontFamily: "JetBrains Mono, monospace", fontSize: "0.82rem" }}
          value={wordsRaw} onChange={e => setWordsRaw(e.target.value)}
          placeholder={"Inc\nLLC\nLtd\nCorp\nPvt Ltd\nPrivate Limited"} />
      </div>
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem", padding: "0.625rem", background: "var(--bg-subtle)", borderRadius: "0.5rem" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.82rem", cursor: "pointer" }}>
          <input data-testid="ct-whole-word" type="checkbox" checked={wholeWord} onChange={e => setWholeWord(e.target.checked)} />
          Whole-word only <span style={{ color: "var(--text-muted)", fontSize: "0.72rem" }}>(e.g. "Inc" won't match "Income")</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.82rem", cursor: "pointer" }}>
          <input data-testid="ct-case-sensitive" type="checkbox" checked={caseSensitive} onChange={e => setCaseSensitive(e.target.checked)} />
          Case sensitive
        </label>
      </div>
      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
        <button data-testid="ct-cancel" onClick={onCancel} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
        <button data-testid="ct-save" onClick={submit} disabled={saving} className="btn-primary" style={{ fontSize: "0.85rem" }}>{saving ? "Saving..." : "Save"}</button>
      </div>
    </div>
  );
}

export default function CleanupTablesPage() {
  const { user } = useAuth();
  const readOnly = useReadOnly();
  const [tables, setTables] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cleanups/");
      setTables(r.data);
      if (user?.role === "superadmin") {
        const d = await api.get("/admin/departments");
        setDepartments(d.data);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (body) => {
    if (editing && editing !== "new" && editing.id) await api.put(`/cleanups/${editing.id}`, body);
    else await api.post("/cleanups/", body);
    setEditing(null); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this cleanup table?")) return;
    await api.delete(`/cleanups/${id}`);
    load();
  };

  return (
    <Layout>
      <div className="animate-in" data-testid="cleanup-tables-page">
        <ReadOnlyBanner what="cleanup tables" />
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1.5rem" }}>
          <div>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.75rem", color: "var(--text-primary)", margin: 0 }}>Cleanup Tables</h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginTop: "0.25rem" }}>
              Lists of words/phrases to strip from column values before matching
            </p>
          </div>
          {!readOnly && (
            <button data-testid="ct-new-btn" className="btn-primary" onClick={() => setEditing("new")} style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.85rem" }}>
              <Plus size={14} /> New Cleanup Table
            </button>
          )}
        </div>

        <div className="recon-card" style={{ padding: 0, overflow: "hidden" }}>
          {loading ? <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>Loading...</div>
          : tables.length === 0 ? (
            <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
              <Eraser size={32} style={{ color: "var(--text-muted)", margin: "0 auto 0.75rem", opacity: 0.4 }} />
              <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", margin: 0 }}>
                No cleanup tables yet. <button onClick={() => setEditing("new")} style={{ color: "var(--sage)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Create one</button>
              </p>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Name", "Description", "Department", "Words", "Options", "Updated", "Actions"].map(h => <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>)}</tr></thead>
              <tbody>
                {tables.map(t => (
                  <tr key={t.id} className="table-row" data-testid={`ct-row-${t.id}`}>
                    <td className="table-cell" style={{ fontWeight: 500 }}>{t.name}</td>
                    <td className="table-cell" style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>{t.description || "—"}</td>
                    <td className="table-cell" style={{ color: "var(--text-muted)" }}>{t.department_name || "—"}</td>
                    <td className="table-cell">
                      <span style={{ background: "#FFF7ED", color: "#9A3412", borderRadius: "9999px", padding: "0.125rem 0.5rem", fontSize: "0.75rem", fontWeight: 600, border: "1px solid #FED7AA" }}>
                        {(t.words || []).length}
                      </span>
                    </td>
                    <td className="table-cell" style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                      {t.whole_word ? "whole-word" : "substring"}{t.case_sensitive ? " · case-sensitive" : ""}
                    </td>
                    <td className="table-cell" style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>{t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—"}</td>
                    <td className="table-cell">
                      <div style={{ display: "flex", gap: "0.375rem" }}>
                        <button data-testid={`ct-edit-${t.id}`} onClick={() => setEditing(t)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><Edit2 size={14} /></button>
                        <button data-testid={`ct-delete-${t.id}`} onClick={() => del(t.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ marginTop: "1.25rem", padding: "0.875rem 1rem", background: "#FFF7ED", borderRadius: "0.5rem", border: "1px solid #FED7AA" }}>
          <p style={{ fontSize: "0.8rem", color: "#9A3412", margin: 0, lineHeight: 1.6 }}>
            <strong>How cleanup works:</strong> Attach a cleanup table to a rule as a transform (<em>cleanup: &lt;name&gt;</em>). Before matching, every occurrence of any word in the list is stripped from the column value. Use whole-word mode to avoid matching substrings inside larger words.
          </p>
        </div>
      </div>

      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === "new" ? "New Cleanup Table" : `Edit: ${editing?.name || ""}`}>
        {editing !== null && <CleanupForm initial={editing === "new" ? null : editing} departments={departments} userRole={user?.role} onSave={save} onCancel={() => setEditing(null)} />}
      </Modal>
    </Layout>
  );
}
